import { useCallback, useEffect, useState } from 'react';
import { useGetIdentity } from 'ra-core';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { EVENTO_FEED, listarPosts, meusVistos } from './feedApi';
import { naoLidos, type Post } from './feedLogica';

/** De quanto em quanto tempo o sino confere se chegou post novo. */
const INTERVALO_MS = 2 * 60 * 1000;

/**
 * Os posts do feed que eu ainda não vi — a fonte do sino e do cartão do
 * Início geral. "Visto" mora no banco (feed_visualizacoes), então vale em
 * qualquer aparelho, ao contrário do ponto do briefing (localStorage).
 *
 * Perfis de leitura (migration 184) não entram no feed: o hook nem consulta.
 */
export function useFeedNaoLidos() {
  const { identity } = useGetIdentity();
  const somenteLeitura = identity?.perfil_leitura != null;
  const [estado, setEstado] = useState<{ posts: Post[]; ultimo: Post | null; carregando: boolean }>({
    posts: [],
    ultimo: null,
    carregando: true,
  });

  const recarregar = useCallback(async () => {
    if (somenteLeitura) return setEstado({ posts: [], ultimo: null, carregando: false });
    try {
      const { data } = await supabase.auth.getSession();
      const eu = data.session?.user.id ?? null;
      const posts = await listarPosts(40);
      const vistos = eu ? await meusVistos(eu, posts.map((p) => p.id)) : new Set<string>();
      const ids = new Set(naoLidos(posts, vistos, eu));
      setEstado({
        posts: posts.filter((p) => ids.has(p.id)),
        ultimo: posts[0] ?? null,
        carregando: false,
      });
    } catch {
      // Feed fora do ar (ou migration ainda não aplicada): o sino só fica quieto.
      setEstado({ posts: [], ultimo: null, carregando: false });
    }
  }, [somenteLeitura]);

  useEffect(() => {
    recarregar();
    const t = window.setInterval(recarregar, INTERVALO_MS);
    window.addEventListener(EVENTO_FEED, recarregar);
    window.addEventListener('focus', recarregar);
    return () => {
      window.clearInterval(t);
      window.removeEventListener(EVENTO_FEED, recarregar);
      window.removeEventListener('focus', recarregar);
    };
  }, [recarregar]);

  return { ...estado, total: estado.posts.length, somenteLeitura, recarregar };
}
